import "./App.css";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useSearchParams } from "react-router-dom";
import AuthenticatedLayout from "./components/layout/AuthenticatedLayout";
import { useAuth } from "./hooks/useAuth";
import AdultGigsPage from "./pages/adult/gigs";
import AdultGigApplicantsPage from "./pages/adult/gig-applicants";
import BusinessListingApplicantsPage from "./pages/adult/business-listing-applicants";
import AdultGuardianMyTeensPage from "./pages/adult/guardian/teen";
import AdultGuardianTeenDetailPage from "./pages/adult/guardian/teendetail";
import AdultReviewsPage from "./pages/adult/reviews";
import AdultScheduledGigsPage from "./pages/adult/scheduled-gigs";
import AdminPage from "./pages/admin";
import FeedbackPage from "./pages/feedback";
import LandingPage from "./pages/landingpage";
import LoginPage from "./pages/login";
import NotificationsPage from "./pages/notifications";
import GigsPage from "./pages/gigs";
import PublicGigPreviewPage from "./pages/gigs/PublicGigPreviewPage";
import ResetPasswordPage from "./pages/reset-password";
import SettingsPage from "./pages/settings";
import SignupPage from "./pages/signup";
import TermsPage from "./pages/terms";
import DashboardPage from "./pages/dashboard";
import VerifyEmailPage from "./pages/verify-email";
import VerifyEmailPendingPage from "./pages/verify-email-pending";
import AdminBusinessVolunteerApprovalPage from "./pages/admin/business-volunteer-approval";
import AdminEmailModerationApprovalPage from "./pages/admin/email-moderation-approval";
import TeenGuardiansPage from "./pages/teen/guardian";
import TeenBusinessApplicationsPage from "./pages/teen/business-applications";
import TeenGigsPage from "./pages/teen/gigs";
import TeenExperienceReportPage from "./pages/teen/experience-report";
import TeenReviewsPage from "./pages/teen/reviews";
import TeenScheduledGigsPage from "./pages/teen/scheduled-gigs";
import { getDefaultAuthenticatedPath, hasAnyRole } from "./utils/AuthUtils";

const RequireAuthenticated = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, status } = useAuth();
  const location = useLocation();

  if (status === "idle" || status === "loading") {
    return (
      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-myteengig-text-muted">Loading your account...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return children;
};

const RequireRole = ({
  allowedRoles,
  children,
}: {
  allowedRoles: string[];
  children: ReactNode;
}) => {
  const { user } = useAuth();

  const allowed = hasAnyRole(user?.roles, allowedRoles);
  if (!allowed) {
    return <Navigate to={getDefaultAuthenticatedPath(user?.roles)} replace />;
  }

  return children;
};

const LegacyAdultRequestsRedirect = () => <Navigate to="/adult/gigs?view=NEEDS_APPROVAL" replace />;

const GigsRoute = () => {
  const { isAuthenticated, status } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sharedGigId = searchParams.get("gigId");

  if (status === "idle" || status === "loading") {
    return (
      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-myteengig-text-muted">Loading your account...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    if (sharedGigId) {
      return <PublicGigPreviewPage />;
    }

    const redirect = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  return (
    <AuthenticatedLayout>
      <RequireRole allowedRoles={["TEEN", "ADULT"]}>
        <GigsPage />
      </RequireRole>
    </AuthenticatedLayout>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-email/pending" element={<VerifyEmailPendingPage />} />
      <Route path="/gigs" element={<GigsRoute />} />
      <Route
        element={
          <RequireAuthenticated>
            <AuthenticatedLayout />
          </RequireAuthenticated>
        }
      >
        <Route
          path="/dashboard"
          element={
            <RequireRole allowedRoles={["TEEN", "ADULT"]}>
              <DashboardPage />
            </RequireRole>
          }
        />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route
          path="/adult/gigs"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultGigsPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/gigs/:gigId/applicants"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultGigApplicantsPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/business-listings/:listingId/applicants"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <BusinessListingApplicantsPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/requests"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <LegacyAdultRequestsRedirect />
            </RequireRole>
          }
        />
        <Route
          path="/adult/scheduled-gigs"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultScheduledGigsPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/reviews"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultReviewsPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/guardian/teens"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultGuardianMyTeensPage />
            </RequireRole>
          }
        />
        <Route
          path="/adult/guardian/teens/:teenId"
          element={
            <RequireRole allowedRoles={["ADULT"]}>
              <AdultGuardianTeenDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/gigs"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenGigsPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/business-applications"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenBusinessApplicationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/scheduled-gigs"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenScheduledGigsPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/guardians"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenGuardiansPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/experience-report"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenExperienceReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/teen/reviews"
          element={
            <RequireRole allowedRoles={["TEEN"]}>
              <TeenReviewsPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireRole allowedRoles={["ADMIN_READ", "ADMIN_WRITE"]}>
              <AdminPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/email-moderation/approve"
          element={
            <RequireRole allowedRoles={["ADMIN_WRITE"]}>
              <AdminEmailModerationApprovalPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/business-volunteer-approval"
          element={
            <RequireRole allowedRoles={["ADMIN_WRITE"]}>
              <AdminBusinessVolunteerApprovalPage />
            </RequireRole>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route
        path="*"
        element={
          <Navigate to="/" replace />
        }
      />
    </Routes>
  );
}

export default App;
